import * as bi from 'react-bootstrap-icons';
import { MessagePoint } from '..';
import { Flex, Input, Message } from 'components';
import { ModalCalendar, SpoilerCard } from 'shared/components';
import { useEffect, useState } from 'react';
import {
  foreignPassportClearFormatting,
  foreignPassportFormat,
  foreignPassportIsValid,
  isPassportExpirationDateValid,
  isPassportIssueDateValid,
} from './helpers';
import { dateFormat } from 'common/helpers';
import { IForeignPassportInput } from '../../types';
import { IClientPassengerFullForeignPassport } from '@bustickus/models-client';

interface IProps {
  open: boolean;
  onOpen: () => void;
  onChange: (passport: IForeignPassportInput | null) => void;
  initial?: IClientPassengerFullForeignPassport | null;
}

function ForeignPassportCard(props: IProps) {
  const [number, setNumber] = useState<string>(foreignPassportFormat(props.initial?.number ?? ''));
  const [issueDate, setIssueDate] = useState<Date | null>(
    props.initial != null ? new Date(props.initial.issueDate) : null
  );
  const [expireDate, setExpireDate] = useState<Date | null>(
    props.initial != null ? new Date(props.initial.expirationDate) : null
  );

  const [numberTouched, setNumberTouched] = useState<boolean>(false);
  const [issueDateTouched, setIssueDateTouched] = useState<boolean>(false);
  const [expireDateTouched, setExpireDateTouched] = useState<boolean>(false);

  const [issueDateModal, setIssueDateModal] = useState<boolean>(false);
  const [expireDateModal, setExpireDateModal] = useState<boolean>(false);

  useEffect(() => {
    handleDataChange(number, issueDate, expireDate);
  }, []);

  const handleNumberChange = (value: string) => {
    if (!numberTouched) {
      setNumberTouched(true);
    }

    setNumber(foreignPassportFormat(foreignPassportClearFormatting(value)));
    handleDataChange(value, issueDate, expireDate);
  };

  const handleIssueDateChange = (value: Date) => {
    if (!issueDateTouched) {
      setIssueDateTouched(true);
    }

    if (issueDateModal) {
      setIssueDateModal(false);
    }

    setIssueDate(value);
    handleDataChange(number, value, expireDate);
  };

  const handleExpireDateChange = (value: Date) => {
    if (!expireDateTouched) {
      setExpireDateTouched(true);
    }

    if (expireDateModal) {
      setExpireDateModal(false);
    }

    setExpireDate(value);
    handleDataChange(number, issueDate, value);
  };

  const handleDataChange = (passportNumber: string, issueDate: Date | null, expireDate: Date | null) => {
    if (
      issueDate != null &&
      expireDate != null &&
      isPassportIssueDateValid(issueDate) &&
      isPassportExpirationDateValid(issueDate, expireDate) &&
      foreignPassportIsValid(passportNumber)
    ) {
      props.onChange({
        number: foreignPassportClearFormatting(passportNumber),
        issueDate: issueDate,
        expirationDate: expireDate,
      });
    } else {
      props.onChange(null);
    }
  };

  const isNumberError = !foreignPassportIsValid(number);
  const isIssueDateError = !isPassportIssueDateValid(issueDate);
  const isExpireDateError = !isPassportExpirationDateValid(issueDate, expireDate);
  const isInputError = isExpireDateError || isIssueDateError || isNumberError;

  return (
    <SpoilerCard
      open={props.open}
      onOpenClick={props.onOpen}
      title='Заграничный паспорт'
    >
      {issueDateModal && (
        <ModalCalendar
          onClose={() => setIssueDateModal(false)}
          onSelect={handleIssueDateChange}
          date={issueDate ?? new Date(2020, 0, 1)}
        />
      )}

      {expireDateModal && (
        <ModalCalendar
          onClose={() => setExpireDateModal(false)}
          onSelect={handleExpireDateChange}
          date={expireDate ?? new Date(2020, 0, 1)}
        />
      )}

      <Flex
        direction='column'
        gap={10}
      >
        <Flex
          direction='column'
          gap={5}
        >
          <Input
            type='text'
            label='Серия и номер'
            placeholder='71 1234567'
            icon={<bi.Passport />}
            value={number}
            onChange={handleNumberChange}
            error={numberTouched && isNumberError}
          />

          <Flex gap={5}>
            <Input
              readonly
              type='text'
              label='Дата выдачи'
              placeholder='Нажмите для выбора'
              value={issueDate != null ? dateFormat(issueDate, 'DD MMMM YYYY').datetime : ''}
              onClick={() => setIssueDateModal(true)}
              error={issueDateTouched && isIssueDateError}
            />

            <Input
              readonly
              type='text'
              label='Дата истечения'
              placeholder='Нажмите для выбора'
              value={expireDate != null ? dateFormat(expireDate, 'DD MMMM YYYY').datetime : ''}
              onClick={() => setExpireDateModal(true)}
              error={expireDateTouched && isExpireDateError}
            />
          </Flex>
        </Flex>

        {isInputError && (
          <Message type='error'>
            <Flex
              direction='column'
              gap={5}
            >
              {isNumberError && <MessagePoint type='error'>Номер заграничного паспорта указан неверно!</MessagePoint>}
              {isIssueDateError && (
                <MessagePoint type='error'>Дата выдачи заграничного паспорта указана неверно!</MessagePoint>
              )}

              {isExpireDateError && (
                <MessagePoint type='error'>
                  Дата истечения заграничного паспорта указана неверно, либо паспорт просрочен!
                </MessagePoint>
              )}
            </Flex>
          </Message>
        )}

        {!isInputError && (
          <Message type='success'>
            <MessagePoint type='success'>
              <b>Отлично!</b> Данные загранпаспорта выглядят корректно!
            </MessagePoint>
          </Message>
        )}
      </Flex>
    </SpoilerCard>
  );
}

export default ForeignPassportCard;
